import { useEffect, useMemo, useRef, useState } from 'react';

/*
  Type-to-search picker.

  Written rather than using <datalist>, which renders through the operating
  system: it ignores the page's styling entirely and arrives as a dark native
  box that looks nothing like the rest of the form. This is plain markup, so it
  matches everything else and behaves the same on every browser.

  Selection is always one of the given options — the typed text is only ever a
  filter, never a value. Callers get an option object or null.
*/

export interface ComboboxOption {
  id: number;
  name: string;
  /** Options sharing a group are listed under it as a heading, in the order given. */
  group?: string;
  /** Other words the search finds it by, not shown: a locality's short form. */
  keywords?: string;
}

export default function Combobox({
  id,
  options,
  value,
  onChange,
  placeholder,
  className = '',
  clearLabel = 'Clear',
}: {
  id: string;
  options: ComboboxOption[];
  value: ComboboxOption | null;
  onChange: (option: ComboboxOption | null) => void;
  placeholder?: string;
  className?: string;
  /** Names what is being cleared, for the button screen readers announce. */
  clearLabel?: string;
}) {
  /*
    `query` is what the box shows, always — including the chosen name once
    something is picked. An earlier version swapped the text out depending on
    whether the list was open, which made the field look like it emptied itself
    the moment you clicked into it.
  */
  const [query, setQuery] = useState(value?.name ?? '');
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const wrapper = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const listId = `${id}-listbox`;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Showing the chosen name is not a filter — reopening should offer
    // everything again, not just the one row that matches what is displayed.
    if (!q || q === value?.name.toLowerCase()) return options;
    return options.filter((o) => `${o.name} ${o.keywords ?? ''}`.toLowerCase().includes(q));
  }, [query, options, value]);

  useEffect(() => {
    setHighlighted(0);
  }, [query, open]);

  /*
    Follow the value while the list is closed. The options often arrive after
    the value does — the trainee editor loads the record and the batch list
    separately — and the box was seeded before there was a name to show, so it
    sat empty despite a batch being set. Only while closed: open, the text is
    the user's search and must not be overwritten under them.
  */
  const valueName = value?.name ?? '';
  useEffect(() => {
    if (!open) setQuery(valueName);
  }, [valueName, open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (wrapper.current && !wrapper.current.contains(e.target as Node)) {
        setOpen(false);
        // Half-typed text that matched nothing would otherwise sit in the box
        // looking like a selection.
        setQuery(value?.name ?? '');
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, value]);

  const choose = (option: ComboboxOption) => {
    onChange(option);
    setQuery(option.name);
    setOpen(false);
  };

  const clear = () => {
    onChange(null);
    setQuery('');
    setOpen(false);
    input.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setHighlighted((i) => {
        if (filtered.length === 0) return 0;
        return (i + step + filtered.length) % filtered.length;
      });
      return;
    }
    if (e.key === 'Enter' && open) {
      // Only swallow Enter when there is something to pick, so the key still
      // submits the form once a choice has been made.
      const option = filtered[highlighted];
      if (option) {
        e.preventDefault();
        choose(option);
      }
      return;
    }
    if (e.key === 'Escape' && open) {
      e.preventDefault();
      setOpen(false);
      // Abandoning the search puts back whatever was chosen before it.
      setQuery(value?.name ?? '');
    }
  };

  return (
    <div ref={wrapper} className="relative">
      <input
        ref={input}
        id={id}
        type="text"
        role="combobox"
        autoComplete="off"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && filtered[highlighted] ? `${id}-opt-${filtered[highlighted].id}` : undefined}
        placeholder={placeholder}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          // Typing after a choice clears it, so a stale selection cannot be
          // submitted alongside different-looking text.
          if (value) onChange(null);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        className={className}
      />

      {query ? (
        <button
          type="button"
          onClick={clear}
          aria-label={clearLabel}
          className="absolute inset-y-0 right-0 grid w-11 place-items-center rounded-r-lg text-ink-400 transition-colors hover:text-ink-900"
        >
          <svg
            aria-hidden
            viewBox="0 0 20 20"
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M5 5l10 10M15 5L5 15" />
          </svg>
        </button>
      ) : (
        /* Chevron mirrors the select control this replaces. */
        <span
          aria-hidden
          className={`pointer-events-none absolute inset-y-0 right-0 grid w-11 place-items-center text-ink-400 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        >
          <svg viewBox="0 0 20 20" className="h-4 w-4" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M5.2 7.5 10 12.3l4.8-4.8 1.2 1.2-6 6-6-6 1.2-1.2Z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      )}

      {open && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-40 mt-1.5 max-h-60 w-full overflow-y-auto rounded-lg border border-ink-200 bg-white py-1 shadow-xl shadow-ink-950/10"
        >
          {filtered.length === 0 ? (
            <li className="px-4 py-3 text-sm text-ink-400">No match</li>
          ) : (
            filtered.map((option, index) => (
              <li key={option.id}>
                {option.group && option.group !== filtered[index - 1]?.group && (
                  <p
                    role="presentation"
                    className={`px-4 pb-1 pt-2.5 text-base font-semibold text-ink-900 ${index > 0 ? 'mt-1 border-t border-ink-100' : ''}`}
                  >
                    {option.group}
                  </p>
                )}
                <button
                  id={`${id}-opt-${option.id}`}
                  type="button"
                  role="option"
                  aria-selected={value?.id === option.id}
                  // pointerdown, not click: the input's blur would otherwise
                  // close the list before the click landed.
                  onPointerDown={(e) => {
                    e.preventDefault();
                    choose(option);
                  }}
                  onMouseEnter={() => setHighlighted(index)}
                  className={`block w-full px-4 py-2.5 text-left text-base transition-colors ${
                    index === highlighted ? 'bg-gold-100 text-gold-700' : 'text-ink-900'
                  } ${value?.id === option.id ? 'font-medium' : ''}`}
                >
                  {option.name}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
